import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Flex, Loader, Modal, Progress, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DATE_FORMATS, parseUiDateDdMmYyyy } from '@dealstack/shared/lib/format/dates/date';
import { BulkPostDatingJobStatus } from '@dealstack/shared/lib/backendClient';
import { documentWorkflowControllerBulkApplyDateStatusOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { createBulkPostDatingWorkflowId } from '@dealstack/shared/lib/common/postDating/workflowId';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useBulkApplyDate } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkApplyDate';
import { useBulkApplyDateStatus } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkApplyDateStatus';
import type { FormValues } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ApplyDateModal.types';
import { createApplyDateSchema } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ApplyDateModal.utils';
import { bucketSkippedByReason } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkApplyDateModal.utils';
import {
	BULK_APPLY_DATE_SKIP_REASON_KEYS,
	BULK_APPLY_DATE_TERMINAL_STATUSES,
	type BulkApplyDateSelection,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkApplyDateModal.types';

interface Props {
	dealId: string;
	programmeId: string;
	selection: BulkApplyDateSelection;
	opened: boolean;
	onClose: () => void;
}

export const BulkApplyDateModal = ({ dealId, programmeId, selection, opened, onClose }: Props) => {
	const tMipManage = useTranslationMipManage();
	// The server derives the job id from the programme, so the modal can ask about a job it
	// never started — that is what lets a reopen, or a page refresh mid-run, re-attach.
	const jobId = createBulkPostDatingWorkflowId(programmeId);
	const [attached, setAttached] = useState(false);
	const queryClient = useQueryClient();
	const statusQueryKey = useMemo(
		() =>
			documentWorkflowControllerBulkApplyDateStatusOptions({
				path: { dealId, programmeId, jobId },
			}).queryKey,
		[dealId, programmeId, jobId]
	);

	const schema = useMemo(() => createApplyDateSchema(tMipManage), [tMipManage]);
	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: { effectiveDate: null },
	});

	const { mutate: startBulkApplyDate, isPending } = useBulkApplyDate({
		dealId,
		programmeId,
		onSuccess: () => {
			// Discards whatever the open-time probe found — a 404 that stopped the poll loop, or a
			// previous run's terminal summary — so the refetch that follows can only be this job.
			void queryClient.resetQueries({ queryKey: statusQueryKey });
			setAttached(true);
		},
	});

	const { data: jobStatus, isError: isStatusError } = useBulkApplyDateStatus({
		dealId,
		programmeId,
		jobId,
		enabled: opened || attached,
		// Only a live job latches. Temporal still answers queries against a closed workflow,
		// so attaching to a terminal one would show a previous batch's summary as this one's.
		onActive: () => setAttached(true),
	});

	const isTerminal = !!jobStatus && BULK_APPLY_DATE_TERMINAL_STATUSES.includes(jobStatus.status);

	const onSubmit = form.handleSubmit(({ effectiveDate }) => {
		if (!effectiveDate) {
			return;
		}
		startBulkApplyDate({
			...selection,
			effectiveDate: format(effectiveDate, DATE_FORMATS.BE_DATE),
		});
	});

	// Resetting only when no job is in flight is what lets a mid-job close reopen onto that
	// job's progress instead of a blank form.
	const handleClose = () => {
		if (!attached || isTerminal) {
			setAttached(false);
			form.reset();
		}
		onClose();
	};

	const renderDateEntry = () => (
		<Stack gap="sm">
			<Text size="14px" c="gray.7">
				{tMipManage('documents_page.bulk_apply_date_modal.description')}
			</Text>

			<Controller
				control={form.control}
				name="effectiveDate"
				render={({ field, fieldState }) => (
					<DateInput
						label={tMipManage('documents_page.bulk_apply_date_modal.date_label')}
						placeholder={tMipManage(
							'documents_page.bulk_apply_date_modal.date_placeholder'
						)}
						value={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						error={fieldState.error?.message}
						valueFormat="DD/MM/YYYY"
						dateParser={parseUiDateDdMmYyyy}
						popoverProps={{ withinPortal: true, zIndex: 1000 }}
						data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_INPUT}
					/>
				)}
			/>

			<Flex justify="flex-end" gap="xs">
				<ButtonV2 size="s" buttonTheme="light" variant="tertiary" onClick={handleClose}>
					{tMipManage('documents_page.bulk_apply_date_modal.cancel')}
				</ButtonV2>
				<ButtonV2
					size="s"
					buttonTheme="light"
					variant="primary"
					disabled={isPending}
					onClick={() => void onSubmit()}
					data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_SUBMIT}
				>
					{tMipManage('documents_page.bulk_apply_date_modal.submit')}
				</ButtonV2>
			</Flex>
		</Stack>
	);

	// The payload's placement counters are for support and debugging only: the admin selected
	// documents, so documents are what gets rendered.
	const renderProgress = () => {
		if (isStatusError) {
			return (
				<Stack gap="md" align="center" p="xl">
					<Text
						size="sm"
						c="red"
						data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_STATUS_ERROR}
					>
						{tMipManage('documents_page.bulk_apply_date_modal.status_error')}
					</Text>
					<ButtonV2 size="s" buttonTheme="light" variant="primary" onClick={handleClose}>
						{tMipManage('documents_page.bulk_apply_date_modal.close_button')}
					</ButtonV2>
				</Stack>
			);
		}

		if (!jobStatus) {
			return (
				<Flex justify="center" align="center" p="xl">
					<Loader />
				</Flex>
			);
		}

		const { totalDocuments, completedDocuments } = jobStatus;
		const percent =
			totalDocuments > 0
				? Math.min(100, Math.round((completedDocuments / totalDocuments) * 100))
				: 0;

		return (
			<Stack gap="md">
				<Text size="sm" data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_PROGRESS_COUNT}>
					{tMipManage('documents_page.bulk_apply_date_modal.progress_label')}
					{': '}
					{completedDocuments}
					{' / '}
					{totalDocuments}
				</Text>
				<Progress value={percent} size="lg" />
				<Text size="xs" c="dimmed">
					{tMipManage('documents_page.bulk_apply_date_modal.background_notice')}
				</Text>
			</Stack>
		);
	};

	const renderSummary = () => {
		if (!jobStatus) {
			return null;
		}
		const isFailed = jobStatus.status === BulkPostDatingJobStatus.FAILED;
		const skippedBuckets = bucketSkippedByReason(jobStatus.skipped);

		return (
			<Stack
				gap="md"
				data-testid={isFailed ? TEST_ELEMENT_IDS.BULK_APPLY_DATE_SUMMARY_FAILED : undefined}
			>
				<Text size="sm" data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_SUMMARY_COUNT}>
					{tMipManage(
						isFailed
							? 'documents_page.bulk_apply_date_modal.summary_failed'
							: 'documents_page.bulk_apply_date_modal.summary_completed'
					)}
					{': '}
					{jobStatus.completedDocuments}
					{' / '}
					{jobStatus.totalDocuments}
				</Text>

				{skippedBuckets.length > 0 && (
					<Stack gap={4} data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_SKIPPED_LIST}>
						<Text size="xs" fw={600}>
							{tMipManage('documents_page.bulk_apply_date_modal.skipped_heading')}
						</Text>
						{skippedBuckets.map(({ reason, count }) => (
							<Text key={reason} size="xs" c="dimmed">
								{tMipManage(BULK_APPLY_DATE_SKIP_REASON_KEYS[reason])}
								{': '}
								{count}
							</Text>
						))}
					</Stack>
				)}

				{jobStatus.failed.length > 0 && (
					<Stack gap={4} data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_FAILED_LIST}>
						<Text size="xs" fw={600}>
							{tMipManage('documents_page.bulk_apply_date_modal.failed_heading')}
						</Text>
						{jobStatus.failed.map((item) => (
							<Text key={item.instanceId} size="xs" c="red">
								{item.error}
							</Text>
						))}
					</Stack>
				)}

				{/* A timed-out run reports zero documents and empty failed/skipped arrays, so this
				    is the only explanation left. Server-formatted, hence no Tolgee key. */}
				{jobStatus.errors.length > 0 && (
					<Stack gap={4} data-testid={TEST_ELEMENT_IDS.BULK_APPLY_DATE_ERRORS_LIST}>
						{jobStatus.errors.map((err, idx) => (
							<Text key={idx} size="xs" c="red">
								{err}
							</Text>
						))}
					</Stack>
				)}

				<Flex justify="flex-end">
					<ButtonV2 size="s" buttonTheme="light" variant="primary" onClick={handleClose}>
						{tMipManage('documents_page.bulk_apply_date_modal.close_button')}
					</ButtonV2>
				</Flex>
			</Stack>
		);
	};

	const renderContent = () => {
		if (!attached) {
			return renderDateEntry();
		}
		if (!isTerminal) {
			return renderProgress();
		}
		return renderSummary();
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={tMipManage('documents_page.bulk_apply_date_modal.title')}
			centered
			size="lg"
		>
			{renderContent()}
		</Modal>
	);
};
